import { type ReactNode, type RefObject } from "react";
import {
  Bold, Italic, Strikethrough, Code, Link as LinkIcon,
  List, ListOrdered, Quote, Table as TableIcon, Code2,
} from "lucide-react";
import { wrapSelection, unwrapSelection, prefixLines } from "../hooks/useMarkdownShortcuts";
import styles from "./MarkdownToolbar.module.css";

interface Props {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (next: string) => void;
  previewOn: boolean;
  onTogglePreview: () => void;
  disabled?: boolean;
}

export function MarkdownToolbar({
  textareaRef, value, onChange, previewOn, onTogglePreview, disabled,
}: Props) {
  const apply = (next: { value: string; start: number; end: number }) => {
    const el = textareaRef.current;
    onChange(next.value);
    if (el) requestAnimationFrame(() => el.setSelectionRange(next.start, next.end));
  };

  const wrap = (delim: string) => () => {
    const el = textareaRef.current;
    if (!el) return;
    const s = el.selectionStart, en = el.selectionEnd;
    const off = unwrapSelection({ value, start: s, end: en, delim });
    apply(off ?? wrapSelection({ value, start: s, end: en, delim }));
  };

  const prefix = (p: string) => () => {
    const el = textareaRef.current;
    if (!el) return;
    const s = el.selectionStart, en = el.selectionEnd;
    const r = prefixLines({ value, start: s, end: en, prefix: p });
    onChange(r.value);
  };

  const insertTable = () => {
    const el = textareaRef.current;
    if (!el) return;
    const s = el.selectionStart;
    const tpl = "\n| col | col |\n|-----|-----|\n| 1   | 2   |\n";
    const next = value.slice(0, s) + tpl + value.slice(s);
    onChange(next);
    if (el) requestAnimationFrame(() => el.setSelectionRange(s + 3, s + 6));
  };

  const insertCodeBlock = () => {
    const el = textareaRef.current;
    if (!el) return;
    const s = el.selectionStart, en = el.selectionEnd;
    const sel = value.slice(s, en);
    const tpl = "```\n" + sel + "\n```";
    const next = value.slice(0, s) + tpl + value.slice(en);
    onChange(next);
    if (el) {
      const caret = s + 4 + sel.length;
      requestAnimationFrame(() => el.setSelectionRange(caret, caret));
    }
  };

  const insertLink = () => {
    const el = textareaRef.current;
    if (!el) return;
    const s = el.selectionStart, en = el.selectionEnd;
    const sel = value.slice(s, en);
    const tpl = sel ? `[${sel}](url)` : `[](url)`;
    const next = value.slice(0, s) + tpl + value.slice(en);
    onChange(next);
    if (el) {
      if (sel) {
        const urlStart = s + sel.length + 3;
        requestAnimationFrame(() => el.setSelectionRange(urlStart, urlStart + 3));
      } else {
        requestAnimationFrame(() => el.setSelectionRange(s + 1, s + 1));
      }
    }
  };

  const btn = (icon: ReactNode, title: string, onClick: () => void) => (
    <button
      type="button"
      className={styles.btn}
      title={title}
      onClick={onClick}
      disabled={disabled}
      tabIndex={-1}
    >
      {icon}
    </button>
  );

  return (
    <div className={styles.bar}>
      {btn(<Bold size={16} />,          "Bold (Ctrl+B)",     wrap("*"))}
      {btn(<Italic size={16} />,        "Italic (Ctrl+I)",   wrap("_"))}
      {btn(<Strikethrough size={16} />, "Strike (Ctrl+Shift+X)", wrap("~"))}
      {btn(<Code size={16} />,          "Inline code (Ctrl+E)", wrap("`"))}
      {btn(<LinkIcon size={16} />,      "Link (Ctrl+K)",     insertLink)}
      {btn(<List size={16} />,          "Bullet list (Ctrl+Shift+8)", prefix("- "))}
      {btn(<ListOrdered size={16} />,   "Numbered list (Ctrl+Shift+7)", prefix("1. "))}
      {btn(<Quote size={16} />,         "Quote (Ctrl+Shift+.)", prefix("> "))}
      {btn(<TableIcon size={16} />,     "Table",             insertTable)}
      {btn(<Code2 size={16} />,         "Code block (Ctrl+Shift+C)", insertCodeBlock)}
      <span className={styles.divider} />
      <div className={styles.toggle}>
        <button
          type="button"
          className={!previewOn ? styles.toggleActive : styles.toggleInactive}
          onClick={previewOn ? onTogglePreview : undefined}
          tabIndex={-1}
        >
          Write
        </button>
        <button
          type="button"
          className={previewOn ? styles.toggleActive : styles.toggleInactive}
          onClick={!previewOn ? onTogglePreview : undefined}
          tabIndex={-1}
        >
          Preview
        </button>
      </div>
    </div>
  );
}
